import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Paintbrush, Eraser, RotateCcw, Check, X, Sliders, ShieldCheck } from 'lucide-react';

interface BackgroundBrushModalProps {
  originalImage: File;
  currentProcessedUrl: string;
  onApply: (newProcessedDataUrl: string) => void;
  onClose: () => void;
}

export function BackgroundBrushModal({
  originalImage,
  currentProcessedUrl,
  onApply,
  onClose,
}: BackgroundBrushModalProps) {
  const [brushMode, setBrushMode] = useState<'restore' | 'erase'>('restore');
  const [brushSize, setBrushSize] = useState<number>(30);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [history, setHistory] = useState<ImageData[]>([]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const originalImgRef = useRef<HTMLImageElement | null>(null);

  // Initialize canvas with current processed image and prepare original image layer
  useEffect(() => {
    const origImg = new Image();
    const origUrl = URL.createObjectURL(originalImage);
    origImg.onload = () => {
      originalImgRef.current = origImg;

      const processedImg = new Image();
      processedImg.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = processedImg.width;
        canvas.height = processedImg.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(processedImg, 0, 0);
        // Save initial state to history
        const initialData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        setHistory([initialData]);
      };
      processedImg.src = currentProcessedUrl;
    };
    origImg.src = origUrl;

    return () => {
      URL.revokeObjectURL(origUrl);
    };
  }, [originalImage, currentProcessedUrl]);

  // Apply brush stroke (restore original pixels or erase to transparent)
  const applyBrush = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current;
      const origImg = originalImgRef.current;
      if (!canvas || !origImg) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      const x = Math.round((clientX - rect.left) * scaleX);
      const y = Math.round((clientY - rect.top) * scaleY);

      const radius = Math.round((brushSize * scaleX) / 2);

      if (brushMode === 'restore') {
        // Draw segment from original image onto processed canvas using a circular clip
        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(origImg, 0, 0, canvas.width, canvas.height);
        ctx.restore();
      } else {
        // Erase to transparent
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    },
    [brushMode, brushSize]
  );

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    applyBrush(e.clientX, e.clientY);
  };

  const [cursorPos, setCursorPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const stageRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => {
    setIsHovering(false);
    setIsDrawing(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      setCursorPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }

    if (!isDrawing) return;
    applyBrush(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height);
        setHistory((prev) => [...prev.slice(-10), snapshot]);
      }
    }
  };

  const handleUndo = () => {
    if (history.length <= 1) return;
    const newHistory = history.slice(0, -1);
    setHistory(newHistory);
    const lastSnapshot = newHistory[newHistory.length - 1];

    const canvas = canvasRef.current;
    if (canvas && lastSnapshot) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.putImageData(lastSnapshot, 0, 0);
      }
    }
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    onApply(dataUrl);
    onClose();
  };

  // Calculate rendered brush circle diameter matching CSS screen pixels
  const displayBrushSize = Math.max(6, brushSize);

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-950/40">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-gradient-to-tr from-pink-600 to-indigo-600 text-white rounded-xl">
              <Paintbrush className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                Magic Restore & Touch-up Brush
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Paint over eyes, text, or details to instantly restore missing parts from your original photo!
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="p-3 bg-slate-100/70 dark:bg-slate-950/60 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBrushMode('restore')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all ${
                brushMode === 'restore'
                  ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30 ring-2 ring-emerald-500/30'
                  : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
              }`}
            >
              <Paintbrush className="w-3.5 h-3.5" />
              <span>🖌️ Restore Original Pixels</span>
            </button>

            <button
              type="button"
              onClick={() => setBrushMode('erase')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all ${
                brushMode === 'erase'
                  ? 'bg-red-600 text-white shadow-md shadow-red-600/30 ring-2 ring-red-500/30'
                  : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
              }`}
            >
              <Eraser className="w-3.5 h-3.5" />
              <span>🧹 Erase to Transparent</span>
            </button>
          </div>

          {/* Brush Size Slider */}
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
              <Sliders className="w-3.5 h-3.5 text-indigo-500" />
              Size: {brushSize}px
            </span>
            <input
              type="range"
              min="5"
              max="150"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              className="w-28 sm:w-40 accent-indigo-600 cursor-pointer"
            />
          </div>

          {/* Undo */}
          <button
            type="button"
            onClick={handleUndo}
            disabled={history.length <= 1}
            className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 text-xs font-semibold rounded-xl hover:bg-slate-200 dark:hover:bg-slate-800 disabled:opacity-40 flex items-center gap-1 transition-all"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Undo</span>
          </button>
        </div>

        {/* Interactive Canvas Stage */}
        <div
          ref={stageRef}
          className="flex-1 p-6 overflow-auto bg-slate-950/90 flex items-center justify-center relative min-h-[350px]"
        >
          <div className="relative border border-slate-800 rounded-2xl overflow-hidden shadow-2xl bg-[radial-gradient(#334155_1px,transparent_1px)] [background-size:16px_16px]">
            <canvas
              ref={canvasRef}
              onMouseEnter={handleMouseEnter}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              className="max-w-full max-h-[60vh] object-contain cursor-none block"
            />

            {/* Dynamic Live Brush Cursor Indicator */}
            {isHovering && (
              <div
                className={`pointer-events-none absolute rounded-full border-2 transform -translate-x-1/2 -translate-y-1/2 shadow-xl ${
                  brushMode === 'restore'
                    ? 'border-emerald-400 bg-emerald-500/20 shadow-emerald-500/40 ring-2 ring-emerald-300/40'
                    : 'border-red-400 bg-red-500/20 shadow-red-500/40 ring-2 ring-red-300/40'
                }`}
                style={{
                  left: `${cursorPos.x}px`,
                  top: `${cursorPos.y}px`,
                  width: `${displayBrushSize}px`,
                  height: `${displayBrushSize}px`,
                }}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-950/40">
          <p className="text-xs text-slate-500 dark:text-slate-400 hidden sm:block">
            💡 Click and drag over eyes or detail areas to instantly bring back original colors.
          </p>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-6 py-2 bg-gradient-to-r from-emerald-600 to-indigo-600 hover:from-emerald-700 hover:to-indigo-700 text-white text-xs font-bold rounded-xl shadow-lg shadow-emerald-500/30 flex items-center gap-1.5 transition-all hover:scale-105"
            >
              <Check className="w-4 h-4" />
              <span>Apply Restored Image</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
